import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthService } from '../../../services/AuthService';
import { Auth } from '../../../Utils/Auth';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import './Login.css';

const Login = () => {
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [rememberMe, setRememberMe] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const navigate = useNavigate();

    // Rediriger si déjà connecté
    useEffect(() => {
        if (Auth.isLoggedIn()) {
            navigate('/dashboard');
        }
    }, [navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Effacer l'erreur quand l'utilisateur tape
        if (error) setError(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const response = await AuthService.login(formData);

            // Vérifier que le login a réussi
            if (response) {
                console.log('Connexion réussie:', response);
                navigate('/dashboard');
            }
        } catch (err) {
            console.error('Erreur de connexion:', err);

            // Gestion spécifique des erreurs avec fallback
            const status = err?.status || err?.response?.status;

            if (status === 403) {
                setError('Accès refusé : rôle non autorisé.');
            } else if (status === 401) {
                setError('Identifiants incorrects. Vérifiez votre email et mot de passe.');
            } else if (status === 429) {
                setError('Trop de tentatives. Veuillez réessayer plus tard.');
            } else if (err?.message === 'Format de réponse invalide') {
                setError('Erreur serveur : format de réponse invalide.');
            } else {
                setError(
                    err?.response?.data?.message ||
                    err?.message ||
                    'Échec de la connexion. Vérifiez vos identifiants.'
                );
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-container">
            {/* Partie gauche - Design */}
            <div className="login-left">
                <div className="login-left-content">
                    <div className="login-logo">
                        <h2 className="text-white text-2xl font-bold">Fraternité Matin</h2>
                        <p className="text-white/80">Gestion des annonces légales</p>
                    </div>

                    <div className="login-illustration">
                        <div className="login-circle"></div>
                        <div className="login-circle-small"></div>
                    </div>

                    <div className="login-text">
                        <h3 className="text-white text-3xl font-bold mb-4">
                            Quelques clics de plus pour vous connecter à votre compte.
                        </h3>
                        <p className="text-white/90 text-lg">
                            Gérez tous vos documents légaux en un seul endroit
                        </p>
                    </div>
                </div>
            </div>

            {/* Partie droite - Formulaire */}
            <div className="login-right">
                <div className="login-form-container">
                    <h1 className="login-title">
                        Connectez-vous !
                    </h1>

                    {error && (
                        <div className="login-error">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="login-form">
                        {/* Champ email */}
                        <div className="input-group">
                            <Mail className="input-icon" />
                            <input
                                className="login-input"
                                placeholder="Email"
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                disabled={loading}
                                required
                            />
                        </div>

                        {/* Champ mot de passe */}
                        <div className="input-group">
                            <Lock className="input-icon" />
                            <input
                                className="login-input"
                                placeholder="Mot de passe"
                                type={showPassword ? "text" : "password"}
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                disabled={loading}
                                required
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                                disabled={loading}
                            >
                                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                            </button>
                        </div>

                        {/* Se souvenir de moi et mot de passe oublié */}
                        <div className="login-options">
                            <div className="remember-me">
                                <input
                                    type="checkbox"
                                    id="remember-me"
                                    className="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    disabled={loading}
                                />
                                <label htmlFor="remember-me" className="checkbox-label">
                                    Se souvenir de moi
                                </label>
                            </div>

                            <Link to="/auth/forgot-password" className="forgot-password">
                                Mot de passe oublié?
                            </Link>
                        </div>

                        {/* Bouton connexion */}
                        <button
                            type="submit"
                            className="login-button"
                            disabled={loading || !formData.email || !formData.password}
                        >
                            {loading ? 'Connexion en cours...' : 'Connexion'}
                        </button>

                        {/* Conditions d'utilisation */}
                        <div className="login-terms">
                            En vous connectant vous acceptez nos
                            <br />
                            <Link to="/terms" className="link">
                                Conditions d'utilisation
                            </Link> &{' '}
                            <Link to="/privacy" className="link">
                                Politique de confidentialité
                            </Link>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Login;